import { yupResolver } from "@hookform/resolvers/yup";
import { useQueryClient } from "@tanstack/react-query";
import classnames from "classnames";
import React, { useCallback, useEffect } from "react";
import { useController, useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import * as Yup from "yup";
import { useShallow } from "zustand/react/shallow";

import { useMobile } from "@krea/common/hooks";
import { settings } from "@krea/common/settings";
import {
  ApplicationForm,
  FORM_STEP,
  useApplicationFormNavigation,
} from "@krea/common/shared-components/application-form";
import { useApplicationFlowForm } from "@krea/common/shared-components/application-form/hooks/useApplicationFlowForm";
import { getDefaultValues } from "@krea/common/shared-components/application-form/utils";
import LoanApplicationFormLayout from "@krea/common/shared-components/loan-application-form-layout";
import { Text } from "@krea/common/shared-components/text";
import { useBankIDStore } from "@krea/common/store/bankID/hooks";
import { useLoanApplicationFormStore } from "@krea/common/store/loan-application-form/hooks";
import { useModalStore } from "@krea/common/store/modal/hooks";
import { BANKID_AUTHENTICATE_STORE_KEY } from "@krea/common/store/queryClient";
import { useGetUserData } from "@krea/common/store/user/hooks/queries";
import { COUNTRY_CODE, termsOfServiceLink } from "@krea/common/utils";

import { useInitiateLoanApplication } from "store/loan-application-form/hooks";

import { ApplicationSummary } from "../../components/application-form/application-summary";
import AllowedCustomerApplicationFlow from "../../components/application-form/fields/allowed-customer-application-flow";
import { CampaignInput } from "../../components/application-form/fields/campaign";
import IdentificationButtonBankId from "../../components/application-form/fields/identification-button-bankid";
import IdentificationButtonSMS from "../../components/application-form/fields/identification-button-sms";
import {
  RemoteIdentificationModalBankID,
  RemoteIdentificationModalSMS,
} from "../../components/modals/remote-identification-modals";
import { RemoteIdentificationType } from "../../utils";

const PartnerApplicationFields = ({ onSubmit, onBack, isPending }) => {
  const { t } = useTranslation();
  const validationSchema = Yup.object().shape({
    utmCampaign: Yup.string().nullable(),
    isCustomerApplicationFlowAllowed: Yup.boolean().required(),
  });

  const {
    organisation,
    remoteIdentification,
    utmCampaign,
    setUtmCampaign,
    setRemoteIdentification,
  } = useLoanApplicationFormStore(
    useShallow((state) => ({
      organisation: state.organisation,
      remoteIdentification: state.remoteIdentification,
      utmCampaign: state.utmCampaign,
      setUtmCampaign: state.setUtmCampaign,
      setRemoteIdentification: state.setRemoteIdentification,
    })),
  );

  const {
    control,
    handleSubmit,
    trigger,
    getValues,
    setValue,
    setError,
    formState,
  } = useForm({
    mode: "onChange",
    defaultValues: {
      utmCampaign: utmCampaign,
      isCustomerApplicationFlowAllowed:
        remoteIdentification.isCustomerApplicationFlowAllowed,
    },
    resolver: yupResolver(validationSchema),
  });

  const utmCampaignController = useController({
    name: "utmCampaign",
    control,
  });

  const isCustomerApplicationFlowAllowedController = useController({
    name: "isCustomerApplicationFlowAllowed",
    control,
  });

  const onUtmCampaignChange = (value) => {
    utmCampaignController.field.onChange(value);
    setUtmCampaign(value);
  };

  const onIsCustomerApplicationFlowAllowedChange = (value) => {
    isCustomerApplicationFlowAllowedController.field.onChange(value);
    setRemoteIdentification({ isCustomerApplicationFlowAllowed: value });
  };

  const onMethodSelect = (remoteIdentificationType) => {
    handleSubmit(
      (data) => {
        onSubmit(remoteIdentificationType);
      },
      (errors) => {
        console.log("invalid", errors);
      },
    )();
  };

  const companyName = organisation.name || "";

  return (
    <LoanApplicationFormLayout
      heading={t("applicationForm.identificationHeading")}
      subheading={t("applicationForm.weAreObligedToIdentifyYou", {
        companyName,
      })}
      upperInfoText={
        settings.countryCode === COUNTRY_CODE.SE
          ? t("applicationForm.common.noApplicationOrCreditCheck")
          : t("applicationForm.common.noApplication")
      }
      innerForm={
        <div className="my-7">
          <CampaignInput
            name="utmCampaign"
            value={utmCampaignController.field.value}
            onChange={(e) => onUtmCampaignChange(e.target.value)}
            errors={
              utmCampaignController.fieldState.isTouched &&
              utmCampaignController.fieldState.error?.message
                ? utmCampaignController.fieldState.error?.message
                : null
            }
          />
          <AllowedCustomerApplicationFlow
            value={isCustomerApplicationFlowAllowedController.field.value}
            handleChange={(e) =>
              onIsCustomerApplicationFlowAllowedChange(e.target.value)
            }
          />

          {/*// Rough start for radio buttons*/}
          {/*<div className="tw-mt-5 tw-flex tw-justify-between">*/}
          {/*  <div className="w-4/12 tw-rounded-lg tw-border-primary">*/}
          {/*    <input type="radio" name="a" id="a" className="invisible" />*/}
          {/*    <label className="tw-h-14" htmlFor="a">*/}
          {/*      {t('applicationForm.common.customer')}*/}
          {/*    </label>*/}
          {/*  </div>*/}
          {/*  <div>*/}
          {/*    <input type="radio" name="a" id="b" className="invisible" />*/}
          {/*    <label htmlFor="b">{t('applicationForm.common.partner')}</label>*/}
          {/*  </div>*/}
          {/*  <div>*/}
          {/*    <input type="radio" name="a" id="c" className="invisible" />*/}
          {/*    <label htmlFor="c">{t('applicationForm.common.both')}</label>*/}
          {/*  </div>*/}
          {/*</div>*/}

          <div className="mt-7">
            {settings.countryCode === COUNTRY_CODE.SE && (
              <>
                <IdentificationButtonBankId
                  variant="contained"
                  disabled={isPending}
                  onClick={() =>
                    onMethodSelect(RemoteIdentificationType.BANK_ID)
                  }
                />
                <div
                  className={classnames(
                    "d-flex justify-content-center shrink-0 order-0 mb-5 mt-7 text-center border-top",
                  )}
                >
                  <Text
                    size="sm"
                    className="tw-mt-[-12px] bg-white font-weight-bold px-2"
                  >
                    {t("applicationForm.commons.formOr")}
                  </Text>
                </div>
              </>
            )}

            <IdentificationButtonSMS
              variant={
                settings.countryCode === COUNTRY_CODE.SE
                  ? "outlined"
                  : "contained"
              }
              disabled={formState.isSubmitting || isPending}
              onClick={() => onMethodSelect(RemoteIdentificationType.SMS)}
            />

            <div className="order-1 d-flex justify-content-center w-100">
              <div className="d-flex align-items-start justify-content-center mt-4">
                <div>
                  <Text size="sm" className="font-weight-bold text-center">
                    {t("applicationForm.commons.subHeading")}
                  </Text>

                  <Text size="sm" className="text-center font-weight-bold pr-1">
                    <Trans i18nKey="applicationForm.thirdPartyTosText">
                      Placeholder: Genom att identifiera dig godkänner du&nbsp;
                      <a
                        target="_blank"
                        href={termsOfServiceLink(
                          null,
                          settings.countryWebsiteUrl,
                          null,
                        )}
                        rel="noopener noreferrer"
                      >
                        Placeholder: användarvillkoren
                      </a>
                    </Trans>
                  </Text>
                </div>
              </div>
            </div>
          </div>
        </div>
      }
      hideNavigation={false}
      hideAdvisorCTABanner
      warningInfoTextIndex={1}
      goToPreviousStep={onBack}
      goToNextStep={null}
    />
  );
};

export const ApplicationFormPage = () => {
  const { t } = useTranslation();
  const isMobile = useMobile();

  const queryClient = useQueryClient();
  const { getPreviousStep } = useApplicationFormNavigation();

  const openModal = useModalStore(({ openModal }) => openModal);

  const resetBankIDStore = useBankIDStore((state) => state.resetBankIDStore);
  const {
    setCurrentStep,
    currentStep,
    remoteIdentificationType,
    setRemoteIdentification,
    resetFormState,
  } = useLoanApplicationFormStore((state) => ({
    currentStep: state.currentStep,
    setCurrentStep: state.setCurrentStep,
    remoteIdentificationType: state.remoteIdentification.type,
    setRemoteIdentification: state.setRemoteIdentification,
    resetFormState: state.resetFormState,
  }));

  const applicationFormProps = useApplicationFlowForm();

  const onFormReset = () => {
    resetFormState();
    const defaultValues = getDefaultValues();
    applicationFormProps.reset(defaultValues);
  };

  const onCancel = () => {
    queryClient.resetQueries({ queryKey: [BANKID_AUTHENTICATE_STORE_KEY] });
    resetBankIDStore();
    setCurrentStep(getPreviousStep());
  };

  const onBack = () => {
    resetBankIDStore();
    setCurrentStep(getPreviousStep());
  };

  const { data: user } = useGetUserData();
  const {
    isInitiated,
    isPending,
    isError,
    initiateLoanApplication,
    cancelApplicationViaBankID,
  } = useInitiateLoanApplication();

  const onSuccessBankID = useCallback(() => {
    initiateLoanApplication();
  }, [initiateLoanApplication]);

  const onSubmit = useCallback(
    (remoteIdentificationType) => {
      // If BankID we need to initiate the call from the BankIDFlow component after filling in the form
      setRemoteIdentification({ type: remoteIdentificationType });

      if (remoteIdentificationType === RemoteIdentificationType.SMS) {
        // If SMS we can initiate the call directly
        initiateLoanApplication();
      }
    },
    [initiateLoanApplication],
  );

  useEffect(() => {
    // Scroll to top on step change on mobile
    if (isMobile) {
      window.scrollTo(0, 0);
    }
  }, [currentStep, isMobile]);

  useEffect(() => {
    if (window.MessageBirdChatWidget) {
      window.MessageBirdChatWidget.hide();
    }

    return () => {
      if (
        window.MessageBirdChatWidget &&
        window.MessageBirdChatWidget.hasOwnProperty("show")
      ) {
        window.MessageBirdChatWidget.show();
      }
    };
  }, []);

  useEffect(() => {
    if (remoteIdentificationType === RemoteIdentificationType.BANK_ID) {
      openModal({
        modal: (
          <RemoteIdentificationModalBankID
            isInitated={isInitiated}
            initiateLoanApplication={initiateLoanApplication}
          />
        ),
        closeOnBackgroundClick: false,
      });
    } else if (
      isPending &&
      remoteIdentificationType === RemoteIdentificationType.SMS
    ) {
      openModal({
        modal: (
          <RemoteIdentificationModalSMS
            isPending={isPending}
            isError={isError}
            cancelApplicationViaBankID={cancelApplicationViaBankID}
          />
        ),
        closeOnBackgroundClick: false,
      });
    }
  }, [
    openModal,
    remoteIdentificationType,
    isInitiated,
    isPending,
    isError,
    initiateLoanApplication,
    cancelApplicationViaBankID,
  ]);

  return (
    <div>
      {currentStep !== FORM_STEP.BASIC_DETAILS && (
        <ApplicationSummary onReset={() => onFormReset()} />
      )}
      {currentStep === FORM_STEP.COMPLETED ? (
        <PartnerApplicationFields
          isPending={isPending}
          onSubmit={onSubmit}
          onBack={onBack}
        />
      ) : (
        <ApplicationForm
          applicationFormProps={applicationFormProps}
          onComplete={() => {}}
        />
      )}
    </div>
  );
};
